/**
 * Capitalization for every piece of Atlas copy, by the role it plays (client/DESIGN.md,
 * "Capitalization"). Names are Title Case; what people read or click is sentence case.
 * Source copy is written in sentence case, so either case can be produced from it: the
 * formatter only ever capitalizes, never lowercases, which is what keeps acronyms, brands and
 * record names intact.
 */
import escapeRegExp from 'lodash/escapeRegExp'

export type Casing = 'title' | 'sentence' | 'none'

/**
 * Change a role's case here and every component follows. Spreadsheet headers are literals;
 * server/tests/common/exportHeaderCasing.test.ts holds them to `columnHeader`.
 */
export const TEXT_ROLE_CASING = {
  hero: 'title',
  display: 'title',
  pageTitle: 'title',
  cardTitle: 'title',
  modalTitle: 'title',
  sectionTitle: 'title',
  title: 'title',
  tab: 'title',
  columnHeader: 'title',
  fieldLabel: 'title',
  eyebrow: 'title',
  button: 'sentence',
  menuItem: 'sentence',
  bodyLg: 'sentence',
  body: 'sentence',
  bodySm: 'sentence',
  caption: 'sentence',
  pill: 'none',
  metric: 'none',
  amount: 'none',
  label: 'title',
} as const satisfies Record<string, Casing>

export type TextRole = keyof typeof TEXT_ROLE_CASING

/** Stay lowercase inside a Title Case name unless they open or close it. */
const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'en',
  'for',
  'from',
  'if',
  'in',
  'into',
  'nor',
  'of',
  'off',
  'on',
  'onto',
  'or',
  'over',
  'per',
  'so',
  'than',
  'the',
  'to',
  'up',
  'via',
  'vs',
  'with',
  'yet',
])

/**
 * Names spelled one way everywhere, in any role, even when the source lowercased them.
 * Short names that are also English words (AIM, AR, AP) are left to the source.
 */
export const PROTECTED_NAMES = [
  'Atlas',
  'Arc',
  'Ongoing Reporting',
  'Due Diligence',
  'Borrowing Base',
  'Loan Committee Report',
  'QuickBooks',
  'DocuSign',
  'Codat',
  'Plaid',
  'Salesforce',
]

const PROTECTED_PATTERN = new RegExp(`\\b(${PROTECTED_NAMES.map(escapeRegExp).join('|')})\\b`, 'gi')
const CANONICAL_NAME = new Map(PROTECTED_NAMES.map((name) => [name.toLowerCase(), name]))

const protectNames = (text: string) =>
  text.replace(PROTECTED_PATTERN, (match) => CANONICAL_NAME.get(match.toLowerCase()) ?? match)

// The first letter only, past opening quotes or brackets: "(optional)" → "(Optional)". Text that
// starts with a number ("2 of 5 selected") is left as written.
const capitalizeFirstLetter = (word: string) =>
  word.replace(
    /^(["'“‘([]*)([a-z])/,
    (_, lead: string, letter: string) => lead + letter.toUpperCase(),
  )

// A word with any capital in it (BBC, QuickBooks, iPhone, Ridgeline) was spelled on purpose.
const isSpelledOnPurpose = (word: string) => /[A-Z]/.test(word)

const titleCaseWord = (word: string, isEdge: boolean, followsColon: boolean) => {
  if (isSpelledOnPurpose(word) && !word.includes('-')) return word
  const bare = word.toLowerCase().replace(/[^a-z]/g, '')
  if (!isEdge && !followsColon && SMALL_WORDS.has(bare)) return word
  return word
    .split('-')
    .map((part) => (isSpelledOnPurpose(part) ? part : capitalizeFirstLetter(part)))
    .join('-')
}

const toTitleCase = (text: string, isContinuation: boolean, isFollowed: boolean) => {
  const tokens = text.split(/(\s+)/)
  const wordIndexes = tokens.flatMap((token, index) => (/\S/.test(token) ? [index] : []))
  const firstWord = wordIndexes[0]
  const lastWord = wordIndexes[wordIndexes.length - 1]
  return tokens
    .map((token, index) => {
      if (!/\S/.test(token)) return token
      const isEdge = (index === firstWord && !isContinuation) || (index === lastWord && !isFollowed)
      const previous = wordIndexes[wordIndexes.indexOf(index) - 1]
      const followsColon = previous !== undefined && /[:—–]$/.test(tokens[previous])
      return titleCaseWord(token, isEdge, followsColon)
    })
    .join('')
}

const toSentenceCase = (text: string, isContinuation: boolean) =>
  isContinuation ? text : capitalizeFirstLetter(text)

const CACHE_LIMIT = 5000
const cache = new Map<string, string>()

/**
 * `isContinuation` is for a run of text that follows another inside one label (the words after
 * an inline <strong>), so it is not treated as the label's first word. `isFollowed` is the
 * mirror: more of the label comes after this run, so its last word is not the label's last.
 */
export const formatCase = (
  casing: Casing,
  text: string,
  {
    isContinuation = false,
    isFollowed = false,
  }: { isContinuation?: boolean; isFollowed?: boolean } = {},
): string => {
  if (casing === 'none' || !text) return text
  const key = `${casing}|${isContinuation ? 1 : 0}${isFollowed ? 1 : 0}|${text}`
  const cached = cache.get(key)
  if (cached !== undefined) return cached
  const named = protectNames(text)
  // A question is a sentence, even where its role names things: "Discard unsaved changes?".
  const isQuestion = /\?\s*$/.test(named)
  const result =
    casing === 'title' && !isQuestion
      ? toTitleCase(named, isContinuation, isFollowed)
      : toSentenceCase(named, isContinuation)
  if (cache.size >= CACHE_LIMIT) cache.clear()
  cache.set(key, result)
  return result
}

/** The case a role's text takes, for copy rendered outside a component (titles, exports, toasts). */
export const formatRole = (role: TextRole, text: string): string =>
  formatCase(TEXT_ROLE_CASING[role], text)
